// ไฟล์: frontend/src/pages/teacher/Growth.js
import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import Chart from 'chart.js/auto';
import { Link } from 'react-router-dom';
import { DEFAULT_AVATAR } from '../../assets/default_avatar';


function TeacherGrowth() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [growthHistory, setGrowthHistory] = useState([]);
  const [growthChart, setGrowthChart] = useState(null);
  
  useEffect(() => {
    fetchClasses();
  }, []);
  
  useEffect(() => {
    // เมื่อเลือกชั้นเรียนและวันที่ ให้โหลดข้อมูลน้ำหนักและส่วนสูง
    if (selectedClassId && selectedDate) {
      fetchGrowthData();
    }
  }, [selectedClassId, selectedDate]);
  
  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      const response = await api.class.getMyClasses();
      const myClasses = response.data.data || [];
      setClasses(myClasses);
      
      // เลือกชั้นเรียนแรกเป็นค่าเริ่มต้น
      if (myClasses.length > 0) {
        setSelectedClassId(myClasses[0].id);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลชั้นเรียนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchGrowthData = async () => {
    try {
      setIsLoading(true);
      const response = await api.growth.getGrowthByClassAndDate(selectedClassId, selectedDate);
      setStudents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching growth data:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลน้ำหนักและส่วนสูงได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchStudentGrowthHistory = async (studentId) => {
    try {
      setIsLoading(true);
      const response = await api.growth.getGrowthByStudent(studentId);
      const historyData = response.data.data.growth_history || [];
      setGrowthHistory(historyData);
      
      // สร้างกราฟเมื่อมีข้อมูล
      if (historyData.length > 0) {
        setTimeout(() => {
          createGrowthChart(historyData);
        }, 300);
      }
    } catch (error) {
      console.error('Error fetching student growth history:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดประวัติการเจริญเติบโตได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGrowthChange = async (studentId, field, value) => {
    // อัปเดตข้อมูลในตารางแสดงผล
    setStudents(prevStudents => 
      prevStudents.map(student => 
        student.student_id === studentId 
          ? { ...student, [field]: value } 
          : student
      )
    );
  };
  
  const handleSaveGrowth = async (student) => {
    try {
      await api.growth.recordGrowth({
        student_id: student.student_id,
        class_id: selectedClassId,
        record_date: selectedDate,
        weight: student.weight,
        height: student.height,
        note: ''
      });
      
      // แสดงข้อความสำเร็จเล็กๆ ด้านมุมขวาบน
      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
      });
      
      Toast.fire({
        icon: 'success',
        title: 'บันทึกข้อมูลเรียบร้อย'
      });
    } catch (error) {
      console.error('Error saving growth data:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกข้อมูลน้ำหนักและส่วนสูงได้',
        confirmButtonColor: '#ff6b01'
      });
    }
  };
  
  const handleBulkSave = async () => {
    try {
      setIsLoading(true);
      
      // ยืนยันการบันทึกทั้งหมด
      const result = await Swal.fire({
        title: 'ยืนยันการบันทึก',
        text: `ยืนยันการบันทึกข้อมูลน้ำหนักและส่วนสูงสำหรับนักเรียนทั้งหมด?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#ff6b01',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก'
      });
      
      if (result.isConfirmed) {
        // บันทึกข้อมูลสำหรับนักเรียนทุกคนที่กรอกข้อมูล
        const promises = students
          .filter(student => student.weight || student.height) // เฉพาะที่มีข้อมูล
          .map(student => 
            api.growth.recordGrowth({
              student_id: student.student_id,
              class_id: selectedClassId,
              record_date: selectedDate,
              weight: student.weight,
              height: student.height,
              note: ''
            })
          );
        
        await Promise.all(promises);
        
        Swal.fire({
          icon: 'success',
          title: 'บันทึกสำเร็จ',
          text: 'บันทึกข้อมูลน้ำหนักและส่วนสูงเรียบร้อยแล้ว',
          confirmButtonColor: '#ff6b01',
          timer: 1500,
          showConfirmButton: false
        });
        
        // โหลดข้อมูลใหม่
        fetchGrowthData();
      }
    } catch (error) {
      console.error('Error bulk saving growth data:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกข้อมูลน้ำหนักและส่วนสูงได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleShowHistory = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
    fetchStudentGrowthHistory(student.student_id);
  };
  
  const createGrowthChart = (historyData) => {
    // เตรียมข้อมูลสำหรับกราฟ
    const sortedData = [...historyData].sort((a, b) => new Date(a.record_date) - new Date(b.record_date));
    const dates = sortedData.map(record => new Date(record.record_date).toLocaleDateString('th-TH'));
    const weights = sortedData.map(record => record.weight);
    const heights = sortedData.map(record => record.height);
    
    // ทำลายกราฟเก่า (ถ้ามี)
    if (growthChart) {
      growthChart.destroy();
    }
    
    // สร้างกราฟใหม่
    const ctx = document.getElementById('growthChart');
    if (!ctx) return;
    
    const newChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: dates,
        datasets: [
          {
            label: 'น้ำหนัก (กก.)',
            data: weights,
            backgroundColor: 'rgba(255, 99, 132, 0.2)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 2,
            yAxisID: 'y-weight'
          },
          {
            label: 'ส่วนสูง (ซม.)',
            data: heights,
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 2,
            yAxisID: 'y-height'
          }
        ]
      },
      options: {
        responsive: true,
        scales: {
          'y-weight': {
            type: 'linear',
            position: 'left',
            title: {
              display: true,
              text: 'น้ำหนัก (กก.)'
            }
          },
          'y-height': {
            type: 'linear',
            position: 'right',
            title: {
              display: true,
              text: 'ส่วนสูง (ซม.)'
            },
            grid: {
              drawOnChartArea: false
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'กราฟแสดงพัฒนาการน้ำหนักและส่วนสูง'
          }
        }
      }
    });
    
    setGrowthChart(newChart);
  };
  
  return (
    <div className="wrapper">
      <Header />
      <Sidebar />
      
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">บันทึกน้ำหนักและส่วนสูง</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/teacher/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">บันทึกน้ำหนักและส่วนสูง</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <section className="content">
          <div className="container-fluid">
            {isLoading ? (
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : (
              <>
                {/* ตัวเลือกชั้นเรียนและวันที่ */}
                <div className="row">
                  <div className="col-12">
                    <div className="card card-orange">
                      <div className="card-header">
                        <h3 className="card-title">เลือกชั้นเรียนและวันที่</h3>
                      </div>
                      <div className="card-body">
                        {classes.length === 0 ? (
                          <div className="alert alert-warning">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            คุณยังไม่ได้รับมอบหมายให้ดูแลชั้นเรียนใดๆ กรุณาติดต่อผู้ดูแลระบบ
                          </div>
                        ) : (
                          <div className="row">
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>ชั้นเรียน</label>
                                <select
                                  className="form-control"
                                  value={selectedClassId}
                                  onChange={(e) => setSelectedClassId(e.target.value)}
                                >
                                  {classes.map(classItem => (
                                    <option key={classItem.id} value={classItem.id}>
                                      {classItem.class_name} (ห้อง {classItem.room_number})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>วันที่บันทึก</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={selectedDate}
                                  onChange={(e) => setSelectedDate(e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* รายการนักเรียนและข้อมูลน้ำหนัก/ส่วนสูง */}
                {classes.length > 0 && (
                  <div className="row">
                    <div className="col-12">
                      <div className="card card-orange">
                      <div className="card-header">
  <h3 className="card-title">บันทึกน้ำหนักและส่วนสูงนักเรียน</h3>
  <div className="card-tools">
    <Link to="/teacher/growth/reports" className="btn btn-tool bg-success">
      <i className="fas fa-chart-pie mr-1"></i> ดูรายงาน
    </Link>
    <button
      type="button"
      className="btn btn-tool"
      data-card-widget="collapse"
    >
      <i className="fas fa-minus"></i>
    </button>
  </div>
</div>
                        <div className="card-body">
                          <div className="mb-3">
                            <button
                              className="btn btn-orange"
                              onClick={handleBulkSave}
                              disabled={isLoading}
                            >
                              <i className="fas fa-save mr-2"></i>
                              บันทึกข้อมูลทั้งหมด
                            </button>
                          </div>
                          
                          {/* รายการนักเรียน */}
                          {students.length === 0 ? (
                            <div className="alert alert-info">
                              <i className="fas fa-info-circle mr-2"></i>
                              ไม่พบข้อมูลนักเรียนในชั้นเรียนนี้
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-bordered table-striped">
                                <thead>
                                  <tr>
                                    <th style={{ width: '60px' }}>ลำดับ</th>
                                    <th style={{ width: '80px' }}>รูปภาพ</th>
                                    <th>ชื่อ-นามสกุล</th>
                                    <th style={{ width: '150px' }}>น้ำหนัก (กก.)</th>
                                    <th style={{ width: '150px' }}>ส่วนสูง (ซม.)</th>
                                    <th style={{ width: '250px' }}>ตัวเลือก</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {students.map((student, index) => (
                                    <tr key={student.student_id}>
                                      <td className="text-center">{index + 1}</td>
                                      <td>
                                        <img
                                          src={`https://cms2568.backend.kruwirat.com/uploads/${student.profile_pic}`}
                                          alt={`${student.first_name} ${student.last_name}`}
                                          className="student-profile-pic-sm"
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = DEFAULT_AVATAR;
                                          }}
                                        />
                                      </td>
                                      <td>
                                        {student.first_name} {student.last_name}
                                        <small className="d-block text-muted">
                                          รหัส: {student.student_code}
                                        </small>
                                      </td>
                                      <td>
                                        <input
                                          type="number"
                                          step="0.1"
                                          min="0"
                                          className="form-control"
                                          value={student.weight || ''}
                                          onChange={(e) => handleGrowthChange(student.student_id, 'weight', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="number"
                                          step="0.1"
                                          min="0"
                                          className="form-control"
                                          value={student.height || ''}
                                          onChange={(e) => handleGrowthChange(student.student_id, 'height', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <button
                                          className="btn btn-success btn-sm mr-1"
                                          onClick={() => handleSaveGrowth(student)}
                                          disabled={!student.weight && !student.height}
                                        >
                                          <i className="fas fa-save"></i> บันทึก
                                        </button>
                                        <button
                                          className="btn btn-info btn-sm"
                                          onClick={() => handleShowHistory(student)}
                                        >
                                          <i className="fas fa-chart-line"></i> ประวัติ
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
      
      {/* Modal แสดงประวัติการเจริญเติบโต */}
      {showModal && selectedStudent && (
        <div className="modal fade show" style={{ display: 'block', paddingRight: '15px' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-orange">
                <h5 className="modal-title">
                  ประวัติการเจริญเติบโตของ {selectedStudent.first_name} {selectedStudent.last_name}
                </h5>
                <button type="button" className="close" onClick={() => setShowModal(false)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {isLoading ? (
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                ) : (
                  <>
                    <div className="row mb-3">
                      <div className="col-md-4 text-center">
                        <img
                          src={`https://cms2568.backend.kruwirat.com/uploads/${selectedStudent.profile_pic}`}
                          alt={`${selectedStudent.first_name} ${selectedStudent.last_name}`}
                          className="student-profile-pic"
                          style={{ width: '120px', height: '120px' }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DEFAULT_AVATAR;
                          }}
                        />
                        <h5 className="mt-2">{selectedStudent.first_name} {selectedStudent.last_name}</h5>
                        <p className="text-muted">รหัสนักเรียน: {selectedStudent.student_code}</p>
                      </div>
                      <div className="col-md-4">
                        <div className="info-box bg-info">
                          <span className="info-box-icon">
                            <i className="fas fa-weight"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">น้ำหนักปัจจุบัน</span>
                            <span className="info-box-number">
                              {selectedStudent.weight ? `${selectedStudent.weight} กก.` : 'ไม่มีข้อมูล'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="info-box bg-success">
                          <span className="info-box-icon">
                            <i className="fas fa-ruler-vertical"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">ส่วนสูงปัจจุบัน</span>
                            <span className="info-box-number">
                              {selectedStudent.height ? `${selectedStudent.height} ซม.` : 'ไม่มีข้อมูล'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* กราฟพัฒนาการน้ำหนักและส่วนสูง */}
                    <div className="chart-container" style={{ height: '300px' }}>
                      <canvas id="growthChart"></canvas>
                    </div>
                    
                    <h5 className="mt-4">ประวัติการวัดน้ำหนักและส่วนสูง</h5>
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>วันที่</th>
                            <th>น้ำหนัก (กก.)</th>
                            <th>ส่วนสูง (ซม.)</th>
                            <th>บันทึกโดย</th>
                            <th>หมายเหตุ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {growthHistory.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center">
                                ไม่พบประวัติการเจริญเติบโต
                              </td>
                            </tr>
                          ) : (
                            growthHistory.map((record) => (
                              <tr key={record.id}>
                                <td>
                                  {new Date(record.record_date).toLocaleDateString('th-TH')}
                                </td>
                                <td>{record.weight} กก.</td>
                                <td>{record.height} ซม.</td>
                                <td>{record.recorded_by_name}</td>
                                <td>{record.note || '-'}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  ปิด
                </button>
              </div>
            </div>
          </div>
          <div className="modal- fade show"></div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}

export default TeacherGrowth;